import React, { useState } from 'react';

const About = ({ language }) => {
  const [activeTab, setActiveTab] = useState('core');
  
  const content = {
    en: {
      title: "About Us",
      subtitle: "Smart Estate Management — Building the Smart Ecosystem for Future Properties",
      intro: "We are a Hong Kong‑rooted technology company focused on in‑depth development and service innovation in the field of Smart Estate Management. With thorough understanding of the local property ecosystem and cutting‑edge technical expertise, we integrate software development, smart hardware and cloud services to provide one‑stop, full‑process digital transformation solutions for property management companies, residential estates and commercial properties.",
      coreTitle: "Core Services",
      coreIntro: "With the vision of \"Smart, Efficient, Transparent\", we have built a complete smart property ecosystem:",
      services: [
        {
          title: "1. EV Charging & Payment System",
          items: [
            "Develop a high‑end electric vehicle charging management platform integrating load balancing and real‑time data monitoring.",
            "Ensure stable, safe and efficient operation of charging stations, and build a sustainable green energy ecosystem for commercial buildings, estates and car parks."
          ]
        },
        {
          title: "2. Cloud‑based Property System",
          items: [
            "Next‑generation management fee software supporting automated calculation, multiple online payment methods, financial reporting and resident data management.",
            "Break time and location constraints through cloud technology, allowing property management teams to precisely oversee operations anytime, anywhere."
          ]
        },
        {
          title: "3. Smart e‑Notification System",
          items: [
            "Replace traditional paper notices, supporting group broadcasting, read receipts and record archiving.",
            "Ensure information reaches every resident accurately, significantly improving community communication efficiency and management transparency while reducing costs."
          ]
        },
        {
          title: "4. Omnichannel Digital Solutions",
          items: [
            "Brand & e‑commerce development: professional website design and development, building responsive official websites and e‑commerce platforms.",
            "Mobile application development: custom iOS and Android applications covering property management, membership systems and mobile office."
          ]
        },
        {
          title: "5. AI‑Powered Smart Marketing",
          items: [
            "Apply artificial intelligence to provide precise customer analysis, smart content generation and advertising optimisation.",
            "Help property managers and merchants effectively reach their target audience, enhance community service value, commercial conversion rate and brand influence."
          ]
        }
      ],
      strengthsTitle: "Our Strengths",
      strengths: [
        "One‑to‑one in‑depth service: a dedicated team provides full support from consultation, planning to development, delivering fully customised Smart Estate Management solutions.",
        "Strong technical capabilities: we focus on system stability, cybersecurity and user experience (UI/UX), ensuring every system is reliable and market‑proven.",
        "Local team, global vision: deeply aligned with Hong Kong market practices while meeting international technical standards, making us a strong partner for corporate digital transformation."
      ],
      visionTitle: "Vision",
      vision: "We strive to become a global leader in Smart Estate Management. We drive property upgrading with technology, build trust with professionalism, and help all partners embrace a more efficient, smart and green new era of property management."
    },
    zh: {
      title: "關於我們",
      subtitle: "Smart Estate Management —— 建構未來物業的智慧生態",
      intro: "我們是一間扎根香港的科技公司，專注於Smart Estate Management（智能物業管理）領域的深度開發與服務創新。憑藉對本地物業生態的透徹理解與前沿的技術實力，我們整合軟件開發、智能硬件與雲端服務，為物業公司、住宅屋苑及商業地產提供一站式、全流程的數碼化轉型方案。",
      coreTitle: "核心業務",
      coreIntro: "我們以「智能、高效、透明」為願景，構建了完整的智能物業生態圈：",
      services: [
        {
          title: "1. 電動車充電及付款系統解決方案",
          items: [
            "研發頂級電動車充電樁管理平台，融合負載平衡系統（Load Balancing）與實時數據監控技術。",
            "確保充電站運營穩定、安全與高效，為商廈、屋苑及停車場創建可持續的綠色能源生態。"
          ]
        },
        {
          title: "2. 全雲端物業管理系統",
          items: [
            "新一代管理費軟件，支援自動計算、多元線上支付、財務报表分析與住戶數據管理。",
            "透過雲端技術，打破時間與空間限制，讓物業管理團隊隨時隨地精準掌控營運數據。"
          ]
        },
        {
          title: "3. 智能通告系統",
          items: [
            "取代傳統紙張公告，支援分組推送、已讀確認與歷史存檔。",
            "確保資訊精準觸達每一位住戶，顯著提升社區溝通效率與管理透明度，節省成本。"
          ]
        },
        {
          title: "4. 全渠道數碼解決方案",
          items: [
            "品牌與電商建設： 專業網頁設計與開發，打造響應式品牌官網與電商平台。",
            "流動應用開發： 客製化開發iOS及Android應用程式，涵蓋物業管理、會員系統及流動辦公。"
          ]
        },
        {
          title: "5. AI + 智能營銷服務",
          items: [
            "運用人工智能技術，提供精準客群分析、智能內容生成與廣告投放優化。",
            "協助物業管理方及商戶，精準觸達目標客群，提升社區服務價值與商業轉化率及品牌影響力。"
          ]
        }
      ],
      strengthsTitle: "我們的優勢",
      strengths: [
        "一對一深度服務： 專屬團隊全程跟進，由諮詢、規劃到開發，提供完全客製化的Smart Estate Management方案。",
        "技術實力過硬： 深耕系統穩定性、網絡安全與用戶體驗（UI/UX），確保每一個系統都能經得起市場考驗。",
        "本地團隊，全球視野： 深度貼合香港市場運營模式，同時具備國際級的技術標準，是企業數碼化轉型的最強伙伴。"
      ],
      visionTitle: "願景",
      vision: "我們致力成為全球Smart Estate Management的領航者。以科技驅動物業升級，以專業建立信任信賴，協助所有合作夥伴邁向高效、智慧、綠色的未來物業新時代。"
    }
  };

  const currentContent = content[language];
  
  const tabs = language === 'en' 
    ? ['Core Services', 'Our Strengths', 'Vision']
    : ['核心業務', '我們的優勢', '願景'];

  return (
    <div className="about__section pt-120 pb-120" id="about">
      <div className="container">
        <div className="about__content__wrapper">
          <div className="section__title text-center mb-5">
            <h2 className="fz-48 fw-700">{currentContent.title}</h2>
            <h3 className="fz-32 fw-600 mt-3" style={{ color: '#c9f31d' }}>
              {currentContent.subtitle}
            </h3>
          </div>

          <div className="about__intro mb-5">
            <p className="pra fz-18" style={{ color:'white', lineHeight: '1.8', maxWidth: '900px', margin: '0 auto', fontWeight:'bold', textAlign: 'justify' }}>
              {currentContent.intro}
            </p>
          </div>

          <div className="singletab">
            <ul className="tablinks">
              <li className={activeTab === 'core' ? 'active' : ''}>
                <button onClick={() => setActiveTab('core')}>
                  {tabs[0]}
                </button>
              </li>
              <li className={activeTab === 'strengths' ? 'active' : ''}>
                <button onClick={() => setActiveTab('strengths')}>
                  {tabs[1]}
                </button>
              </li>
              <li className={activeTab === 'vision' ? 'active' : ''}>
                <button onClick={() => setActiveTab('vision')}>
                  {tabs[2]}
                </button>
              </li>
            </ul>
            
            <div className="tabcontents">
              {/* Core Services Tab */}
              <div className={`tabitem ${activeTab === 'core' ? 'active' : ''}`}>
                <div>
                  <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                    <img 
                      src="/assets/aboutus.jpg" 
                      alt="About Us" 
                      className="about-tab-hero-img"
                      style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(201, 243, 29, 0.1)'
                      }} 
                    />
                  </div>
                  <div>
                    <h2 className="h2-head aos-init aos-animate" data-aos="fade-up" data-aos-duration="500">
                      {currentContent.coreTitle}
                    </h2>
                    <div className="core__services">
                  <p className="pra fz-18 mb-4">{currentContent.coreIntro}</p>

                  <div className="services__list">
                    {currentContent.services.map((service, index) => (
                      <div 
                        key={index} 
                        className="service__item mb-4"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.03)',
                          padding: '2rem',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <h4 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
                          {service.title}
                        </h4>
                        <ul style={{ listStyle: 'none', padding: '0' }}>
                          {service.items.map((item, idx) => (
                            <li key={idx} className="pra fz-16 mb-2" style={{ 
                              paddingLeft: '1.5rem',
                              position: 'relative',
                              lineHeight: '1.7'
                            }}>
                              <span style={{ 
                                position: 'absolute',
                                left: '0',
                                color: '#c9f31d',
                                fontWeight: 'bold'
                              }}>•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Our Strengths Tab */}
              <div className={`tabitem ${activeTab === 'strengths' ? 'active' : ''}`}>
                <div className="about-tab-layout">
                  <div className="about-tab-sidebar">
                    <img 
                      src="/assets/aboutus.jpg" 
                      alt="About Us" 
                      className="about-tab-sidebar-img"
                      style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(201, 243, 29, 0.1)'
                      }} 
                    />
                  </div>
                  <div className="about-tab-content">
                    <h2 className="h2-head aos-init aos-animate" data-aos="fade-up" data-aos-duration="500">
                      {currentContent.strengthsTitle}
                    </h2>
                    <div className="our__strengths">
                  <div className="strengths__grid" style={{ 
                    display: 'grid',
                    gap: '2rem',
                    marginTop: '2rem'
                  }}>
                    {currentContent.strengths.map((strength, index) => (
                      <div 
                        key={index} 
                        className="strength__card"
                        style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          padding: '2rem',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem'
                        }}>
                          <span style={{ 
                            color: '#f39a1d',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            lineHeight: '1'
                          }}>★</span>
                          <p className="pra fz-18" style={{ lineHeight: '1.8', margin: '0', color:'#c9f31d' }}>
                            {strength}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vision Tab */}
              <div className={`tabitem ${activeTab === 'vision' ? 'active' : ''}`}>
                <div className="about-tab-layout">
                  <div className="about-tab-sidebar">
                    <img 
                      src="/assets/aboutus.jpg" 
                      alt="About Us" 
                      className="about-tab-sidebar-img"
                      style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(201, 243, 29, 0.1)'
                      }} 
                    />
                  </div>
                  <div className="about-tab-content">
                    <h2 className="h2-head aos-init aos-animate" data-aos="fade-up" data-aos-duration="500">
                      {currentContent.visionTitle}
                    </h2>
                    <div className="our__vision">
                  <div style={{ 
                    background: 'linear-gradient(135deg, rgba(48, 102, 255, 0.1) 0%, rgba(201, 243, 29, 0.1) 100%)',
                    padding: '3rem',
                    borderRadius: '12px',
                    border: '1px solid rgba(201, 243, 29, 0.2)',
                    textAlign: 'center'
                  }}>
                    <p className="pra fz-20" style={{ lineHeight: '2', margin: '0', color:'#c9f31d' }}>
                      {currentContent.vision}
                    </p>
                  </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
